/**
 * handles.ts —— 句柄登记处
 *
 * IL 只支持 `root.method(args)` 形式的链式调用，没有变量、没有赋值。
 * 但像「找到王二，然后给他发消息」这种两步操作，中间需要传递一个对象。
 * 怎么在无变量的语言里表达？
 *
 *   session.send('王二', '你好')
 *
 * 反直觉但正确的做法是：**不传对象，传名字**。
 * 让 API 自己去解析「王二」这个人 —— 名字本身就是用户能理解和复述的句柄。
 *
 * 但有一类东西没法用名字指代：文件摘要（一条 file_digests 记录）、
 * 一次拍照产生的图片。它们需要短 id。于是有了这个句柄表：
 *
 *   file.summarize('王二')  →  { id: 'digest_3', ... }
 *   ↑ 注册进本表                      ↓
 *   digest.getSummary('digest_3')  ← 用 id 取回
 *
 * 句柄表是**内存态**：刷新页面就清空。这是刻意的 ——
 * 句柄是「此刻对话的上下文」，不该跨会话存活。
 * 持久化的东西（联系人、会话、摘要）永远能通过名字或 id 重新查到。
 *
 * 同时，句柄表承担了另一个职责：**给 AI 一份「你现在能引用什么」的清单**。
 * 每次注册句柄都会更新一份快照，`describeForPrompt()` 会把它拼进提示词，
 * 否则模型拿到 id 也不知道那是啥。
 */

import type { HandleKind } from '@/types/il';

export interface HandleEntry {
  id: string;
  kind: HandleKind;
  /** 给模型看的一句话描述，会进提示词 —— 如「王二的文件摘要（12 个文件）」 */
  label: string;
  /** 真实负载，由各 API 自己解释 */
  payload: unknown;
  createdAt: number;
}

/** 每种句柄最多保留几条 —— 提示词里列太多既费 token 又干扰模型 */
const MAX_PER_KIND = 8;

class HandleRegistry {
  private readonly entries = new Map<string, HandleEntry>();

  /** 登记一个新句柄，返回它的 id */
  add(kind: HandleKind, label: string, payload: unknown): string {
    const id = nextId(kind);
    this.entries.set(id, { id, kind, label, payload, createdAt: Date.now() });
    this.trim(kind);
    return id;
  }

  get(id: string): HandleEntry | undefined {
    return this.entries.get(id);
  }

  /** 取某类句柄里最近的一条（`file.getSummary()` 不传 id 时的兜底） */
  latest(kind: HandleKind): HandleEntry | undefined {
    return this.listOf(kind)[0];
  }

  /** 某类句柄，最近的在前 */
  listOf(kind: HandleKind): HandleEntry[] {
    return [...this.entries.values()]
      .filter((e) => e.kind === kind)
      .sort((a, b) => b.createdAt - a.createdAt);
  }

  remove(id: string): void {
    this.entries.delete(id);
  }

  /**
   * 生成提示词用的句柄清单。
   * 没有句柄时返回空串，由调用方决定是否省略这一节。
   */
  describeForPrompt(): string {
    const lines: string[] = [];
    for (const kind of ['digest', 'image'] as HandleKind[]) {
      const list = this.listOf(kind);
      if (list.length === 0) continue;
      lines.push(`${kind}：`);
      for (const e of list) {
        lines.push(`  ${e.id} —— ${e.label}`);
      }
    }
    return lines.join('\n');
  }

  /** 清空（清数据 / 载入演示数据时调用，避免句柄指向已不存在的记录） */
  clear(): void {
    this.entries.clear();
  }

  private trim(kind: HandleKind): void {
    const list = this.listOf(kind);
    for (const stale of list.slice(MAX_PER_KIND)) {
      this.entries.delete(stale.id);
    }
  }
}

/** 单调递增的序号，让 id 短且可读（digest_1、digest_2…） */
const counters = new Map<HandleKind, number>();
function nextId(kind: HandleKind): string {
  const n = (counters.get(kind) ?? 0) + 1;
  counters.set(kind, n);
  return `${kind}_${n}`;
}

export const handles = new HandleRegistry();
